import { apiClient } from "@/lib/axios";
import {
  ChallengeCampaign,
  ChallengeCampaignWithTotal,
  ChallengeCampaignStatus,
  ChallengeUser,
  ChallengeMetadata,
  GetAllCampaignsResponse,
  WithPagination,
  LeaderboardPeriodType,
} from "@/types";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { SocialUser } from "@/types/social-pay.types";
import { CreateTelegramUser } from "@/components/socials/telegram-login-button";
import { User } from "@/types/profile.types";

// PayID related types
export interface GetPayIdsByAddressRequestDto {
  addresses: string[];
}

export interface PayIdInfo {
  address: string;
  payId: string | null;
}

export interface GetPayIdsByAddressResponseDto {
  success: boolean;
  data: PayIdInfo[];
}

const getDeviceSignature = async () => {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId; // Unique visitor identifier
  } catch (error) {
    console.error("Error fetching device signature:", error);
    return null;
  }
};

export class PulsarService {
  static async getChallengeCampaigns() {
    const { data } =
      await apiClient.get<ChallengeCampaignWithTotal[]>(`/challenges`);
    return data;
  }

  static async getChallengeCampaign(id: string, groupId?: string) {
    const url = groupId
      ? `/challenges/${id}/${groupId}`
      : `/challenges/${id}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static async getChallengeCampaignTaskStatus(id: string, groupId?: string) {
    const url = groupId
      ? `/challenges/${id}/tasks-status/${groupId}`
      : `/challenges/${id}/tasks-status`;
    const { data } = await apiClient.get<ChallengeCampaignStatus>(url);
    return data;
  }

  static async getChallengeCampaignLeaderboard(
    campaignId: string,
    groupId: string | undefined,
    page: number,
    limit: number,
    period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
    date?: string
  ) {
    let url = `/challenges/${campaignId}/leaderboard?page=${page}&limit=${limit}`;
    if (groupId) {
      url = `/challenges/${campaignId}/leaderboard/${groupId}?page=${page}&limit=${limit}`;
    }
    if (period !== LeaderboardPeriodType.ALL) {
      url += `&type=${period}`;
    }
    if (date) {
      url += `&date=${date}`;
    }
    const { data } = await apiClient.get<WithPagination<ChallengeUser>>(url);
    return data;
  }

  static async getChallengeCampaignUser(campaignId: string, groupId?: string) {
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature =
      results[0].status === "fulfilled" ? results[0].value : undefined;

    const url = groupId
      ? `/challenges/${campaignId}/me/${groupId}`
      : `/challenges/${campaignId}/me`;

    const { data } = await apiClient.get<ChallengeUser>(url, {
      headers: deviceSignature
        ? { "X-Device-Signature": deviceSignature }
        : undefined,
    });
    return data;
  }

  static async getChallengeCampaignMetadata(id: string, groupId?: string) {
    const url = groupId
      ? `/challenges/${id}/metadata/${groupId}`
      : `/challenges/${id}/metadata`;
    const { data } = await apiClient.get<ChallengeMetadata>(url);
    return data;
  }

  static async getAllCampaigns() {
    const { data } =
      await apiClient.get<GetAllCampaignsResponse>(`/challenges/all`);
    return data;
  }

  static async getAllCampaignsByGroup(group: string) {
    const { data } = await apiClient.get<GetAllCampaignsResponse>(
      `/challenges/all/${group}`
    );
    return data;
  }

  static async getCampaign(campaignId: string, groupId?: string) {
    const url = groupId
      ? `/challenges/${campaignId}/${groupId}`
      : `/challenges/${campaignId}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static doCampaignTask = async (
    taskGuid: string,
    extraArguments?: string[],
    turnstileToken?: string
  ) => {
    return apiClient.post<{ status: boolean; error?: string }>(
      `/challenges/do-task`,
      { taskGuid, extraArguments: extraArguments || [] },
      turnstileToken
        ? { headers: { "x-turnstile-token": turnstileToken } }
        : undefined
    );
  };

  static createReferral = async (
    campaignId: string,
    groupId: string | undefined,
    referralCode: string
  ) => {
    if (groupId) {
      return apiClient.post<{ success: boolean }>(
        `/challenges/${campaignId}/create-referral/${groupId}`,
        { referralCode }
      );
    }
    return apiClient.post<{ success: boolean }>(
      `/challenges/${campaignId}/create-referral`,
      { referralCode }
    );
  };

  static getSocialUser = async () => {
    return apiClient.get<SocialUser>(`/social-pay/me`);
  };

  static registerTwitter = async (redirectUrl?: string) => {
    return apiClient.post<string>("/social-pay/register/twitter", {
      type: "register",
      redirectUrl,
    });
  };

  static registerTelegram = async (body: CreateTelegramUser) => {
    return apiClient.post<string>("/social-pay/register/telegram", body);
  };

  static unlinkTelegram = async () => {
    return apiClient.delete<string>("/social-pay/register/telegram");
  };

  static unlinkTwitter = async () => {
    return apiClient.delete<string>("/social-pay/unregister");
  };

  // --- Profile Endpoints ---
  static getProfile = async () => {
    return apiClient.get<User | undefined>(`/users`);
  };

  static updateProfile = async (formData: FormData) => {
    return apiClient.put<User>(`/users`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  static linkAccount = async (email: string) => {
    return apiClient.post(`/users/link-email`, { email });
  };

  static verifyEmail = async (code: string) => {
    return apiClient.post(`/users/verify-email`, { code });
  };

  static resendVerificationCode = async (email: string) => {
    return apiClient.post(`/users/resend-verification`, { email });
  };

  static unlinkAccount = async () => {
    return apiClient.post(`/users/unlink-address`);
  };

  static registerDiscord = async (redirectUrl: string) => {
    return apiClient.get<string>(
      `/social-pay/register/discord?redirectUri=${redirectUrl}`
    );
  };

  static unregisterDiscord = async () => {
    return apiClient.delete("/social-pay/register/discord");
  };

  // Game Leaderboard methods
  static async getGameLeaderboard(
    period: string,
    limit: number = 100
  ) {
    const { data } = await apiClient.get(`/game-leaderboard`, {
      params: { period, limit }
    });
    return data;
  }

  static async getGameLeaderboardByTps(
    period: string,
    limit: number = 100
  ) {
    const { data } = await apiClient.get(`/game-leaderboard/by-tps`, {
      params: { period, limit }
    });
    return data;
  }

  static async getGameLeaderboardByScore(
    period: string,
    limit: number = 100
  ) {
    const { data } = await apiClient.get(`/game-leaderboard/by-score`, {
      params: { period, limit }
    });
    return data;
  }
}
